import { useEffect, useState, useRef, useMemo } from "react";
import axios from "axios";
import { MapPin, Map, Grid, BarChart2, ChevronLeft, ChevronRight, Layers, IndianRupee, TrendingUp, Users, X, Award, Building2, Home, User, UserCheck, Download, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import InfoTooltip from "../components/InfoTooltip";
import StatCard from "../components/StatCard";
import { SectionHeader, PageHeader } from "../components/SectionHeader";
import { useFilters } from "../context/FilterContext";
import { buildDashboardParams, serializeFyForApi } from "../utils/dashboardFilters";
import IndiaMap from "../components/IndiaMap";
import { normalizeGeoName, DISTRICT_DB_TO_TOPO } from "../data/geoMappings";
import districtNameToShapeID from "../data/districtNameToShapeID.json";
import GrantsTableSection from "../components/GrantsTableSection";
import StateTrustHeatmap from "../components/StateTrustHeatmap";

import { fmtCr, fmtNum, fmtOutreach, displayTheme } from "../utils/format";
import { getThemeDotColor } from "../utils/dynamicColors";

// Look up district shapeID from a district name
const lookupDistrictShapeID = (districtName) => {
  if (!districtName) return null;
  const raw = districtName.toLowerCase().trim();
  if (districtNameToShapeID[raw]) return districtNameToShapeID[raw];
  const topo = DISTRICT_DB_TO_TOPO[raw];
  if (topo && districtNameToShapeID[topo.toLowerCase().trim()]) {
    return districtNameToShapeID[topo.toLowerCase().trim()];
  }
  const norm = normalizeGeoName(districtName);
  if (districtNameToShapeID[norm]) return districtNameToShapeID[norm];
  return null;
};

export default function GeographicFootprint() {
  const { filters, filterOptions, viewMode, setGlobalLoading, API } = useFilters();
  const sf = filterOptions.data_sources?.fluxx || filterOptions.snowflake_connected;

  const [summary,        setSum]           = useState(null);
  const [topStates,      setTopStates]     = useState([]);
  const [stateTable,     setStateTable]    = useState([]);
  const [districtData,   setDistrictData]  = useState([]);
  const [blockData,      setBlockData]     = useState([]);
  const [selectedState,    setSelState]    = useState(null);
  const [selectedDistrict, setSelDistrict] = useState(null);
  const [selectedDistShapeID, setSelDistShapeID] = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [distLoading, setDistLoad] = useState(false);
  const [blockLoading,setBlockLoad]= useState(false);
  const [showApportionTooltip, setShowApportionTooltip] = useState(false);
  const [zoneSummary, setZoneSummary] = useState([]);
  const [aspirationalData, setAspirationalData] = useState(null);

  // Refs / state to keep Aspirational Districts & Blocks cards bottom-aligned.
  // Districts naturally fits; Blocks gets internal scroll capped to Districts' height.
  const aspDistrictsRef = useRef(null);
  const [aspMatchHeight, setAspMatchHeight] = useState(null);
  const [aspirationalBlocksData, setAspirationalBlocksData] = useState(null);
  const [stateDistTable, setStateDistTable] = useState([]);
  const [districtDistTable, setDistrictDistTable] = useState([]);
  const [distTableTab, setDistTableTab] = useState("state");
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("desc");

  // ── Pan India tab state ───────────────────────────────────────────────────
  const [panIndiaDist, setPanIndiaDist] = useState({ data: [], total: null });
  const [panIndiaGrants, setPanIndiaGrants] = useState([]);
  const [panIndiaGrantsLoading, setPanIndiaGrantsLoading] = useState(false);
  // Which scope's drill-down is open: null | 'pan_india' | 'rail_sahyog' | 'all'
  const [panIndiaDrillScope, setPanIndiaDrillScope] = useState(null);
  const panIndiaDrillRef = useRef(null);

  // Map view toggle: "states" | "districts"
  const [mapViewMode, setMapViewMode] = useState("states");
  const [allDistrictData, setAllDistrictData] = useState([]);
  const [geoDetail, setGeoDetail] = useState(null);
  const grantsTableRef = useRef(null);
  const mapRef = useRef(null);

  const grantsTableFilters = useMemo(() => {
    const f = {};
    const serializedFy = serializeFyForApi(filters.fy);
    if (serializedFy) f.fy = serializedFy;
    if (viewMode) f.view_mode = viewMode;
    if (filters.theme) f.theme = filters.theme;
    if (filters.trust) f.trust = filters.trust;
    if (filters.status) f.status = filters.status;
    if (filters.grantee) f.grantee = filters.grantee;
    if (selectedState) f.state = selectedState;
    if (selectedDistrict) f.district = selectedDistrict;
    return f;
  }, [filters.fy, viewMode, filters.theme, filters.trust, filters.status, filters.grantee, selectedState, selectedDistrict]);

  const scrollToGrantsTable = () => {
    grantsTableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const [geoDetailLoading, setGeoDetailLoading] = useState(false);

  const params = buildDashboardParams(filters, { view_mode: viewMode });

  // ── Fetch base data ────────────────────────────────────────────────────────
  useEffect(() => {
    setLoading(true);
    setGlobalLoading(true);
    Promise.all([
      axios.get(`${API}/geographic/summary`,   { params }),
      axios.get(`${API}/geographic/top-states`,{ params: { ...params, limit: 20 } }),
      axios.get(`${API}/geographic/state-data`,{ params }),
      axios.get(`${API}/geographic/zone-summary`, { params }).catch(() => ({ data: [] })),
      axios.get(`${API}/geographic/aspirational-districts`, { params }).catch(() => ({ data: null })),
      axios.get(`${API}/geographic/state-distribution`, { params }).catch(() => ({ data: { data: [] } })),
      axios.get(`${API}/geographic/aspirational-blocks`, { params }).catch(() => ({ data: null })),
      axios.get(`${API}/geographic/all-district-data`, { params }).catch(() => ({ data: [] })),
      axios.get(`${API}/geographic/district-distribution`, { params }).catch(() => ({ data: { data: [] } })),
      axios.get(`${API}/geographic/pan-india-distribution`, { params }).catch(() => ({ data: { data: [], total: null } })),
    ]).then(([sRes, tsRes, sdRes, zRes, adRes, sdtRes, abRes, allDistRes, ddtRes, piDistRes]) => {
      setSum(sRes.data);
      setTopStates(tsRes.data);
      setStateTable(Array.isArray(sdRes.data) ? sdRes.data : (sdRes.data?.data || []));
      setZoneSummary(Array.isArray(zRes.data) ? zRes.data : []);
      setAspirationalData(adRes.data);
      setStateDistTable(sdtRes.data?.data || []);
      setAspirationalBlocksData(abRes.data);
      setAllDistrictData(allDistRes.data || []);
      setDistrictDistTable(ddtRes.data?.data || []);
      setPanIndiaDist({ data: piDistRes.data?.data || [], total: piDistRes.data?.total || null });
    }).catch(console.error).finally(() => { setLoading(false); setGlobalLoading(false); });
  }, [filters.fy, filters.theme, filters.trust, filters.status, filters.grantee, viewMode]);

  // ── Fetch district data when state selected ────────────────────────────────
  useEffect(() => {
    if (!selectedState) { setDistrictData([]); return; }
    setDistLoad(true);
    axios.get(`${API}/geographic/district-data`, {
      params: { state: selectedState, ...params }
    }).then(r => setDistrictData(r.data))
      .catch(console.error)
      .finally(() => setDistLoad(false));
  }, [selectedState, filters.fy, filters.theme, filters.trust, filters.status, filters.grantee, viewMode]);

  // ── Fetch block data when district selected ────────────────────────────────
  useEffect(() => {
    if (!selectedDistrict || !selectedState) { setBlockData([]); return; }
    setBlockLoad(true);
    axios.get(`${API}/geographic/block-data`, {
      params: { state: selectedState, district: selectedDistrict, ...params }
    }).then(r => setBlockData(r.data))
      .catch(console.error)
      .finally(() => setBlockLoad(false));
  }, [selectedDistrict, selectedState, filters.fy, filters.theme, filters.trust, filters.status, filters.grantee, viewMode]);

  const handleStateSelect = (state) => {
    setSelState(state || null);
    setSelDistrict(null);
    setBlockData([]);
  };

  const handleDistrictSelect = (district, shapeID) => {
    setSelDistrict(district || null);
    setSelDistShapeID(shapeID || null);
    if (!district) setBlockData([]);
  };

  // Click on # of Grants cell in State/District distribution tables → scroll to grants table
  const handleGrantsCellClick = (state, district = null) => {
    if (!state) return;
    setSelState(state);
    setSelDistrict(district || null);
    // Wait one tick for geoDetail fetch to mount the GrantsTableSection, then smooth-scroll
    setTimeout(() => {
      grantsTableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 350);
  };

  // Open the Pan India drill-down for a given scope and fetch rows.
  const openPanIndiaDrill = (scope = "pan_india") => {
    setPanIndiaDrillScope(scope);
    setPanIndiaGrantsLoading(true);
    axios.get(`${API}/geographic/pan-india-grants`, { params: { ...params, scope } })
      .then(r => setPanIndiaGrants(r.data?.data || []))
      .catch(() => setPanIndiaGrants([]))
      .finally(() => {
        setPanIndiaGrantsLoading(false);
        setTimeout(() => panIndiaDrillRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
      });
  };

  // Re-fetch the Pan India drill-down whenever filters change while it is open.
  useEffect(() => {
    if (!panIndiaDrillScope) return;
    setPanIndiaGrantsLoading(true);
    axios.get(`${API}/geographic/pan-india-grants`, { params: { ...params, scope: panIndiaDrillScope } })
      .then(r => setPanIndiaGrants(r.data?.data || []))
      .catch(() => setPanIndiaGrants([]))
      .finally(() => setPanIndiaGrantsLoading(false));
    // Intentionally depend on raw filter fields, not `params` (new reference every render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.fy, filters.theme, filters.trust, filters.status, filters.grantee, viewMode, panIndiaDrillScope]);

  // Fetch geo detail panel when state/district selection changes
  useEffect(() => {
    if (!selectedState) { setGeoDetail(null); return; }
    setGeoDetailLoading(true);
    const detailParams = { ...params, state: selectedState };
    if (selectedDistrict) detailParams.district = selectedDistrict;
    axios.get(`${API}/geographic/geo-detail`, { params: detailParams })
      .then(r => setGeoDetail(r.data))
      .catch(console.error)
      .finally(() => setGeoDetailLoading(false));
  }, [selectedState, selectedDistrict, filters.fy, filters.theme, filters.trust, filters.status, filters.grantee, viewMode]);

  // Observe Aspirational Districts card height so we can match Blocks card to it.
  useEffect(() => {
    if (!aspDistrictsRef.current) return;
    const update = () => {
      if (aspDistrictsRef.current) {
        setAspMatchHeight(aspDistrictsRef.current.getBoundingClientRect().height);
      }
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(aspDistrictsRef.current);
    window.addEventListener("resize", update);
    return () => { ro.disconnect(); window.removeEventListener("resize", update); };
  }, [aspirationalData]);


  // ── Active panel data ──────────────────────────────────────────────────────
  const panelData   = selectedDistrict ? blockData     : selectedState ? districtData : topStates;
  const panelKey    = selectedDistrict ? "block"       : selectedState ? "district"   : "state";
  const panelTitle  = selectedDistrict ? `${selectedDistrict} — Blocks`
                    : selectedState    ? `${selectedState} — Districts`
                    : "Top States";
  const panelLoading = selectedDistrict ? blockLoading : selectedState ? distLoading : false;

  return (
    <div data-testid="geographic-page" className="space-y-6">
      <PageHeader title="Geographic Footprint" badge="04" />

      {/* Map + Detail Panel side by side */}
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-5">
        {/* Map (left, 2 cols — compact like Grants Overview) */}
        <div className={`${geoDetail ? "xl:col-span-2" : "xl:col-span-5"} bg-white rounded-xl border border-slate-200 shadow-sm p-4 transition-all`} data-testid="choropleth-section">
          {sf ? (
            <IndiaMap
              ref={mapRef}
              stateData={stateTable}
              districtData={districtData}
              blockData={blockData}
              allDistrictData={allDistrictData}
              onStateSelect={handleStateSelect}
              onDistrictSelect={handleDistrictSelect}
              selectedState={selectedState}
              selectedDistrict={selectedDistrict}
              selectedDistShapeID={selectedDistShapeID}
              dataLoading={distLoading || blockLoading}
              mapViewMode={mapViewMode}
              onMapViewChange={(mode) => { setMapViewMode(mode); if (selectedState) { setSelState(null); setSelDistrict(null); setDistrictData([]); setBlockData([]); setGeoDetail(null); } }}
              showZoomControls={true}
            />
          ) : (
            <div className="bg-slate-50 rounded-lg flex items-center justify-center" style={{ height: 480 }}>
              <div className="text-center">
                <MapPin size={48} className="text-slate-500 mx-auto mb-3" />
                <div className="text-sm text-slate-500 font-medium">Geographic data requires FLUXX connection</div>
              </div>
            </div>
          )}
        </div>

        {/* Detail Panel (right, 3 cols — larger like Grants Overview) */}
        {geoDetail && (
          <div className="xl:col-span-3 bg-white rounded-xl border border-slate-200 shadow-sm overflow-y-auto" style={{ maxHeight: 720 }} data-testid="geo-detail-panel">
            {geoDetailLoading ? (
              <div className="flex items-center justify-center h-40">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#B71C1C]" />
              </div>
            ) : (
              <div className="p-6 space-y-5">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">{geoDetail.label}</h3>
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      {geoDetail.status_breakdown?.map((s, i) => (
                        <span key={i} className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${
                          s.status === "Ongoing" ? "bg-green-50 text-green-700 border-green-200" :
                          s.status === "Closed" ? "bg-slate-100 text-slate-600 border-slate-200" :
                          "bg-amber-50 text-amber-700 border-amber-200"
                        }`}>{s.status}: {s.count}</span>
                      ))}
                      <button onClick={scrollToGrantsTable}
                        className="group text-[10px] font-bold px-2 py-1 rounded-full bg-slate-800 text-white hover:bg-[#B71C1C] hover:scale-110 hover:shadow-lg hover:ring-2 hover:ring-[#B71C1C]/30 transition-all duration-200 cursor-pointer"
                        data-testid="geo-grants-chip" title="View grants table">
                        {geoDetail.grants} Grants <span className="inline-block transition-transform duration-200 group-hover:translate-y-0.5">&darr;</span>
                      </button>
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">{geoDetail.portfolio_pct}% Portfolio</span>
                    </div>
                  </div>
                  <button onClick={() => { setGeoDetail(null); setSelState(null); setSelDistrict(null); setDistrictData([]); setBlockData([]); }}
                    className="text-slate-600 hover:text-slate-600 p-0.5"><X size={16} /></button>
                </div>

                {/* KPIs */}
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: "SANCTIONED", value: `₹${geoDetail.sanctioned_cr} Cr`, color: "text-[#B71C1C]" },
                    { label: "DISBURSED", value: `₹${geoDetail.disbursed_cr} Cr`, sub: `${geoDetail.disbursement_pct}%`, color: "text-emerald-700" },
                  ].map((kpi, i) => (
                    <div key={i} className="bg-slate-50 rounded-lg border border-slate-100 px-4 py-3">
                      <div className="text-[10px] font-semibold text-slate-600 uppercase tracking-wider">{kpi.label}</div>
                      <div className={`text-base font-bold mt-1 ${kpi.color}`}>
                        {kpi.value}
                        {kpi.sub && <span className="text-xs font-medium text-slate-600 ml-1">({kpi.sub})</span>}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Thematic Coverage */}
                {geoDetail.theme_breakdown?.length > 0 && (
                  <div className="bg-slate-50/60 rounded-xl border border-slate-100 p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Layers size={14} className="text-slate-600" />
                      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Thematic Coverage</span>
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border bg-slate-100 text-slate-500 border-slate-200">{geoDetail.theme_breakdown.length} themes</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {geoDetail.theme_breakdown.map((t, i) => (
                        <span key={i} className="text-xs font-medium px-2.5 py-1 rounded-full border border-slate-200 bg-white text-slate-700 flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: getThemeDotColor(t.theme) }} />
                          {displayTheme(t.theme)} <span className="text-[#B71C1C] font-bold">({t.count})</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Outreach */}
                {geoDetail.outreach?.grant_count > 0 && (
                  <div className="bg-slate-50/60 rounded-xl border border-slate-100 p-5">
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      <Users size={16} className="text-slate-600" />
                      <span className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Outreach</span>
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border bg-slate-100 text-slate-500 border-slate-200">
                        from {geoDetail.outreach.grant_count} of {geoDetail.grants} grants
                      </span>
                    </div>
                    <div className="grid grid-cols-4 gap-2.5">
                      {[
                        { icon: Building2, value: geoDetail.outreach.institutions, label: "Institutions", bg: "bg-blue-50", fg: "text-blue-600" },
                        { icon: UserCheck, value: geoDetail.outreach.user_groups, label: "User Groups", bg: "bg-violet-50", fg: "text-violet-600" },
                        { icon: Home, value: geoDetail.outreach.households, label: "HHs", bg: "bg-amber-50", fg: "text-amber-600" },
                        { icon: User, value: geoDetail.outreach.individuals, label: "Individuals", bg: "bg-green-50", fg: "text-green-600" },
                      ].map(({ icon: Icon, value, label, bg, fg }, i) => (
                        <div key={i} className="bg-white rounded-lg border border-slate-100 px-3 py-4 text-center">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-2 ${bg}`}>
                            <Icon size={18} className={fg} />
                          </div>
                          <div className="text-xl font-bold text-slate-800" title={(value || 0).toLocaleString("en-IN")}>{fmtOutreach(value)}</div>
                          <div className="text-xs text-slate-500 mt-1">{label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Key Performance Indicators (KPIs) — RAG Cards */}
                {geoDetail.meas_grant_count > 0 && (
                  <div className="bg-slate-50/60 rounded-xl border border-slate-100 p-5">
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      <TrendingUp size={14} className="text-slate-600" />
                      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Key Performance Indicators (KPIs)</span>
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border bg-slate-100 text-slate-500 border-slate-200">
                        from {geoDetail.meas_grant_count} of {geoDetail.grants} grants
                      </span>
                    </div>
                    {geoDetail.rag_counts && geoDetail.rag_counts.total > 0 ? (
                      <div className="grid grid-cols-4 gap-3" data-testid="geo-rag-kpi">
                        <div className="bg-white rounded-lg border-l-4 border-blue-500 border border-slate-200 px-3 py-3 text-center">
                          <div className="text-2xl font-bold text-blue-600">{geoDetail.rag_counts.blue}</div>
                          <div className="text-[10px] font-semibold text-blue-700 uppercase tracking-wider mt-1">Blue</div>
                          <div className="text-[9px] text-slate-500 mt-0.5">&gt; 100%</div>
                        </div>
                        <div className="bg-white rounded-lg border-l-4 border-green-500 border border-slate-200 px-3 py-3 text-center">
                          <div className="text-2xl font-bold text-green-600">{geoDetail.rag_counts.green}</div>
                          <div className="text-[10px] font-semibold text-green-700 uppercase tracking-wider mt-1">Green</div>
                          <div className="text-[9px] text-slate-500 mt-0.5">96–100%</div>
                        </div>
                        <div className="bg-white rounded-lg border-l-4 border-amber-500 border border-slate-200 px-3 py-3 text-center">
                          <div className="text-2xl font-bold text-amber-600">{geoDetail.rag_counts.amber}</div>
                          <div className="text-[10px] font-semibold text-amber-700 uppercase tracking-wider mt-1">Amber</div>
                          <div className="text-[9px] text-slate-500 mt-0.5">81–95%</div>
                        </div>
                        <div className="bg-white rounded-lg border-l-4 border-red-500 border border-slate-200 px-3 py-3 text-center">
                          <div className="text-2xl font-bold text-red-600">{geoDetail.rag_counts.red}</div>
                          <div className="text-[10px] font-semibold text-red-700 uppercase tracking-wider mt-1">Red</div>
                          <div className="text-[9px] text-slate-500 mt-0.5">0–80%</div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-xs text-slate-600 italic">No KPI data available</div>
                    )}
                  </div>
                )}

                {/* Top Grantees */}
                {geoDetail.top_grantees?.length > 0 && (
                  <div className="bg-slate-50/60 rounded-xl border border-slate-100 p-5">
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      <Award size={14} className="text-slate-600" />
                      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Top Grantees by Sanctioned</span>
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border bg-slate-100 text-slate-500 border-slate-200">{geoDetail.total_grantees} total</span>
                    </div>
                    <div className="space-y-1">
                      {geoDetail.top_grantees.map((g, i) => (
                        <div key={i} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100 last:border-0">
                          <span className="text-slate-700 font-medium truncate flex-1 mr-2">{i + 1}. {g.name}</span>
                          <span className="text-slate-600 mr-3 flex-shrink-0">{g.grants} grants</span>
                          <span className="font-bold text-slate-800 flex-shrink-0">₹{g.sanctioned_cr}Cr</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Zone Summary - hidden */}
      {false && zoneSummary.length > 0 && (
        <SectionHeader title="Zone Summary" subtitle="Proportional allocation by operational zone">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="zone-summary">
            {zoneSummary.map((z, i) => (
              <div key={i} className="bg-white border border-slate-100 rounded-xl p-3 text-center">
                <div className="text-xs font-medium text-slate-500 mb-1">{z.zone}</div>
                <div className="text-base font-bold text-[#B71C1C]">{fmtCr(z.allocation_cr)}</div>
                <div className="text-xs text-slate-600 mt-0.5">{z.pct_of_total}% of portfolio</div>
                <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full bg-[#B71C1C] rounded-full transition-all" style={{ width: `${z.pct_of_total}%` }} />
                </div>
                <div className="text-xs text-slate-600 mt-1">{z.grants} grants · {z.state_count} states</div>
              </div>
            ))}
          </div>
        </SectionHeader>
      )}

      {/* Aspirational Districts & Blocks side by side — bottoms aligned via measured height */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
        {aspirationalData && (
          <div ref={aspDistrictsRef} className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid="asp-districts-card-wrap">
            <div className="mb-1">
              <h3 className="text-sm font-semibold text-slate-900">Aspirational Districts</h3>
              <p className="text-[11px] text-slate-600">NITI Aayog 2018 — 112 districts across 28 states</p>
            </div>
            <div className="flex items-center gap-5 mb-3 mt-3">
              <div className="text-center">
                <div className="text-2xl font-bold text-[#B71C1C]">{aspirationalData.covered}<span className="text-sm text-slate-600 font-normal">/{aspirationalData.total}</span></div>
                <div className="text-[10px] text-slate-500">Covered</div>
              </div>
              <div className="flex-1">
                <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full bg-[#B71C1C] rounded-full" style={{ width: `${aspirationalData.pct_covered}%` }} />
                </div>
              </div>
            </div>
            <div className="columns-1 md:columns-2 gap-1.5 space-y-1.5" data-testid="asp-districts-state-list">
              {(aspirationalData.by_state || []).map((s, i) => (
                <details key={i} className="group bg-slate-50 hover:bg-slate-100/60 rounded-lg text-xs transition-colors break-inside-avoid" data-testid={`asp-district-state-${s.state}`}>
                  <summary className="flex items-center justify-between px-2.5 py-1.5 cursor-pointer list-none select-none">
                    <span className="flex items-center gap-1 min-w-0">
                      <ChevronRight size={12} className="text-slate-400 group-open:rotate-90 transition-transform flex-shrink-0" />
                      <span className="font-medium text-slate-700 truncate">{s.state}</span>
                    </span>
                    <span className="text-[#B71C1C] font-bold ml-2 flex-shrink-0">{s.covered}/{s.total_in_state}</span>
                  </summary>
                  {Array.isArray(s.districts) && s.districts.length > 0 && (
                    <ul className="px-3 pb-2 pt-0.5 space-y-0.5 border-t border-slate-200 bg-white/60">
                      {s.districts.map((d, di) => (
                        <li key={di} className="text-[11px] text-slate-600 pl-4 leading-relaxed flex items-center gap-1">
                          <span className="text-[#B71C1C]">•</span>
                          <span>{d}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </details>
              ))}
            </div>
          </div>
        )}

        {aspirationalBlocksData && (
          <div
            className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 flex flex-col overflow-hidden"
            style={aspMatchHeight ? { height: aspMatchHeight } : undefined}
            data-testid="asp-blocks-card-wrap"
          >
            <div className="mb-1 flex-shrink-0">
              <h3 className="text-sm font-semibold text-slate-900">Aspirational Blocks</h3>
              <p className="text-[11px] text-slate-600">NITI Aayog V1.0 — 499 blocks across 30+ states</p>
            </div>
            <div className="flex items-center gap-5 mb-3 mt-3 flex-shrink-0">
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">{aspirationalBlocksData.covered}<span className="text-sm text-slate-600 font-normal">/{aspirationalBlocksData.total}</span></div>
                <div className="text-[10px] text-slate-500">Covered</div>
              </div>
              <div className="flex-1">
                <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full bg-purple-600 rounded-full" style={{ width: `${aspirationalBlocksData.pct_covered}%` }} />
                </div>
              </div>
            </div>
            <div className="columns-1 md:columns-2 gap-1.5 space-y-1.5 overflow-y-auto pr-1 min-h-0 flex-1" data-testid="asp-blocks-state-list">
              {(aspirationalBlocksData.by_state || []).map((s, i) => (
                <details key={i} className={`group rounded-lg text-xs transition-colors break-inside-avoid ${s.covered > 0 ? "bg-slate-50 hover:bg-slate-100/60" : "bg-slate-50/60 hover:bg-slate-100/60"}`} data-testid={`asp-block-state-${s.state}`}>
                  <summary className="flex items-center justify-between px-2.5 py-1.5 cursor-pointer list-none select-none">
                    <span className="flex items-center gap-1 min-w-0">
                      <ChevronRight size={12} className="text-slate-400 group-open:rotate-90 transition-transform flex-shrink-0" />
                      <span className="font-medium text-slate-700 truncate">{s.state}</span>
                    </span>
                    <span className={`font-bold ml-2 flex-shrink-0 ${s.covered > 0 ? "text-purple-600" : "text-slate-600"}`}>{s.covered}/{s.total_in_state}</span>
                  </summary>
                  {Array.isArray(s.blocks) && s.blocks.length > 0 && (
                    <ul className="px-3 pb-2 pt-0.5 space-y-0.5 border-t border-slate-200 bg-white/60">
                      {s.blocks.map((b, bi) => (
                        <li key={bi} className="text-[11px] text-slate-600 pl-4 leading-relaxed flex items-center gap-1">
                          <span className="text-purple-600">•</span>
                          <span><span className="font-medium text-slate-800">{b.block}</span>
                            <span className="text-slate-500"> · {b.district}</span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </details>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* State × Trust Investment Intensity Heatmap */}
      <SectionHeader
        title="Investment Intensity — State × Trust"
        subtitle="Cumulative sanctioned grant value by state, broken down by Trust. Multi-state grants apportioned proportionally."
        collapsible
        defaultOpen={true}
      >
        <StateTrustHeatmap onPanIndiaClick={() => { setDistTableTab("pan_india"); openPanIndiaDrill("pan_india"); }} />
      </SectionHeader>

      {/* State/District-wise Distribution Table */}
      {(() => {
        const toggleSort = (col) => {
          if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
          else { setSortCol(col); setSortDir("desc"); }
        };
        const SortIcon = ({ col }) => sortCol === col
          ? (sortDir === "asc" ? <ArrowUp size={10} className="inline ml-0.5" /> : <ArrowDown size={10} className="inline ml-0.5" />)
          : <ArrowUpDown size={10} className="inline ml-0.5 opacity-30" />;
        const sortData = (data) => {
          if (!sortCol) return data;
          return [...data].sort((a, b) => {
            let va = a[sortCol], vb = b[sortCol];
            if (typeof va === "string") { va = va.toLowerCase(); vb = (vb || "").toLowerCase(); }
            if (va == null) return 1; if (vb == null) return -1;
            return sortDir === "asc" ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
          });
        };
        const Th = ({ col, children, align = "right" }) => (
          <th className={`text-${align} font-medium text-slate-500 uppercase py-2 px-2 cursor-pointer hover:text-[#B71C1C] transition-colors select-none`}
            onClick={() => toggleSort(col)}>
            {children}<SortIcon col={col} />
          </th>
        );
        const sortedState = sortData(stateDistTable);
        const sortedDistrict = sortData(districtDistTable);
        return (
      <SectionHeader
        title={distTableTab === "state" ? "State-wise Distribution" : distTableTab === "district" ? "District-wise Distribution" : "Pan India Distribution"}
        subtitle={distTableTab === "pan_india" ? "Grants and sanctioned amounts for nation-wide reach grants." : undefined}
        collapsible defaultOpen={false}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1">
            <button onClick={() => { setDistTableTab("state"); setSortCol(null); }} data-testid="dist-tab-state"
              className={`text-[10px] px-2.5 py-1 rounded-full border font-medium transition-colors ${distTableTab === "state" ? "bg-[#B71C1C] text-white border-[#B71C1C]" : "bg-white text-slate-600 border-slate-200 hover:border-[#B71C1C]"}`}>
              State
            </button>
            <button onClick={() => { setDistTableTab("district"); setSortCol(null); }} data-testid="dist-tab-district"
              className={`text-[10px] px-2.5 py-1 rounded-full border font-medium transition-colors ${distTableTab === "district" ? "bg-[#B71C1C] text-white border-[#B71C1C]" : "bg-white text-slate-600 border-slate-200 hover:border-[#B71C1C]"}`}>
              District
            </button>
            <button onClick={() => { setDistTableTab("pan_india"); setSortCol(null); }} data-testid="dist-tab-pan-india"
              className={`text-[10px] px-2.5 py-1 rounded-full border font-medium transition-colors ${distTableTab === "pan_india" ? "bg-[#B71C1C] text-white border-[#B71C1C]" : "bg-white text-slate-600 border-slate-200 hover:border-[#B71C1C]"}`}>
              Pan India
            </button>
          </div>
          <button
            data-testid="export-dist-csv"
            onClick={() => {
              if (distTableTab === "state") {
                const headers = ["SN","State","Zone","Grants","Sanctioned (Cr)","Disbursed (Cr)","Disb %","Districts","Blocks","Aspirational Dist.","Aspirational Blocks"];
                const rows = sortedState.map((r, i) => [
                  i+1, r.state, r.zone || "", r.grants, r.allocation_cr || "",
                  r.disbursed_cr || "", r.disbursement_rate != null ? r.disbursement_rate + "%" : "",
                  r.districts || "", r.blocks || "",
                  r.ad_total > 0 ? `${r.ad_covered || 0}/${r.ad_total}` : "",
                  r.ab_total > 0 ? `${r.ab_covered || 0}/${r.ab_total}` : ""
                ]);
                const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(",")).join("\n");
                const blob = new Blob([csv], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a"); a.href = url; a.download = "state_wise_distribution.csv"; a.click();
                URL.revokeObjectURL(url);
              } else if (distTableTab === "pan_india") {
                const headers = ["SN","Scope","Description","Grants","Sanctioned (Cr)","Disbursed (Cr)","Disb %"];
                const rows = panIndiaDist.data.map(r => [
                  r.sn, r.scope, r.description || "", r.grants, r.sanctioned_cr, r.disbursed_cr,
                  r.disbursement_rate != null ? r.disbursement_rate + "%" : ""
                ]);
                if (panIndiaDist.total) {
                  rows.push(["", "Total", "", panIndiaDist.total.grants, panIndiaDist.total.sanctioned_cr,
                             panIndiaDist.total.disbursed_cr,
                             panIndiaDist.total.disbursement_rate != null ? panIndiaDist.total.disbursement_rate + "%" : ""]);
                }
                const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(",")).join("\n");
                const blob = new Blob([csv], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a"); a.href = url; a.download = "pan_india_distribution.csv"; a.click();
                URL.revokeObjectURL(url);
              } else {
                const headers = ["SN","State","District","Grants","Sanctioned (Cr)","Disbursed (Cr)","Disb %","Blocks","Aspirational"];
                const rows = sortedDistrict.map((r, i) => [
                  i+1, r.state, r.district, r.grants, r.allocation_cr || "",
                  r.disbursed_cr || "", r.disbursement_rate != null ? r.disbursement_rate + "%" : "",
                  r.blocks || "", r.is_aspirational ? "Yes" : "No"
                ]);
                const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(",")).join("\n");
                const blob = new Blob([csv], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a"); a.href = url; a.download = "district_wise_distribution.csv"; a.click();
                URL.revokeObjectURL(url);
              }
            }}
            className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-[#B71C1C] hover:text-[#B71C1C] transition-colors"
          >
            <Download size={12} />
            Export CSV
          </button>
        </div>
        <div className="overflow-x-auto">
          {distTableTab === "pan_india" ? (
          <table className="w-full text-xs" data-testid="pan-india-distribution-table">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="text-left font-medium text-slate-500 uppercase py-2 px-2 w-8">SN</th>
                <th className="text-left font-medium text-slate-500 uppercase py-2 px-2">Scope</th>
                <th className="text-right font-medium text-slate-500 uppercase py-2 px-2">Grants</th>
                <th className="text-right font-medium text-slate-500 uppercase py-2 px-2">Sanctioned</th>
                <th className="text-right font-medium text-slate-500 uppercase py-2 px-2">Disbursed</th>
                <th className="text-right font-medium text-slate-500 uppercase py-2 px-2">Disb %</th>
              </tr>
            </thead>
            <tbody>
              {panIndiaDist.data.length === 0 && (
                <tr><td colSpan={6} className="text-center text-slate-500 py-8 text-sm">No Pan India grants for the current filters.</td></tr>
              )}
              {panIndiaDist.data.map((r) => (
                <tr key={r.scope_key} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                  <td className="py-2 px-2 text-slate-400 text-[11px]">{r.sn}</td>
                  <td className="py-2 px-2 font-medium text-slate-800">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${r.scope_key === "pan_india" ? "bg-red-50 text-[#B71C1C] border-red-200" : "bg-blue-50 text-blue-700 border-blue-200"}`}>
                      {r.scope}
                    </span>
                    <span className="text-slate-500 text-[11px] ml-2">{r.description}</span>
                  </td>
                  <td className="py-2 px-2 text-right">
                    <button
                      type="button"
                      data-testid={`pan-india-grants-link-${r.scope_key}`}
                      onClick={() => openPanIndiaDrill(r.scope_key)}
                      className="text-[#B71C1C] font-medium underline decoration-dotted underline-offset-2 hover:decoration-solid hover:text-[#8a0e0e] transition-colors cursor-pointer"
                      title={`View ${r.scope} grant list`}
                    >
                      {r.grants}
                    </button>
                  </td>
                  <td className="py-2 px-2 text-right font-medium text-[#B71C1C]">{fmtCr(r.sanctioned_cr)}</td>
                  <td className="py-2 px-2 text-right text-slate-600">{fmtCr(r.disbursed_cr)}</td>
                  <td className="py-2 px-2 text-right">
                    {r.disbursement_rate != null ? (
                      <span className={`px-1.5 py-0.5 rounded ${r.disbursement_rate >= 70 ? "bg-green-100 text-green-700" : r.disbursement_rate >= 40 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                        {r.disbursement_rate}%
                      </span>
                    ) : "–"}
                  </td>
                </tr>
              ))}
              {panIndiaDist.total && (
                <tr className="bg-slate-50 border-t border-slate-200 font-semibold">
                  <td></td>
                  <td className="py-2 px-2 text-slate-800">Total</td>
                  <td className="py-2 px-2 text-right text-slate-800">{panIndiaDist.total.grants}</td>
                  <td className="py-2 px-2 text-right text-[#B71C1C]">{fmtCr(panIndiaDist.total.sanctioned_cr)}</td>
                  <td className="py-2 px-2 text-right text-slate-700">{fmtCr(panIndiaDist.total.disbursed_cr)}</td>
                  <td className="py-2 px-2 text-right">
                    {panIndiaDist.total.disbursement_rate != null ? (
                      <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">{panIndiaDist.total.disbursement_rate}%</span>
                    ) : "–"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          ) : distTableTab === "state" ? (
          <table className="w-full text-xs" data-testid="state-distribution-table">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="text-left font-medium text-slate-500 uppercase py-2 px-2 w-8">SN</th>
                <Th col="state" align="left">State</Th>
                <Th col="zone" align="left">Zone</Th>
                <Th col="grants">Grants</Th>
                <Th col="allocation_cr">Sanctioned</Th>
                <Th col="disbursed_cr">Disbursed</Th>
                <Th col="disbursement_rate">Disb %</Th>
                <Th col="districts">Districts</Th>
                <Th col="blocks">Blocks</Th>
                <th className="text-right font-medium text-slate-500 uppercase py-2 px-2 group relative cursor-help">
                  <span className="border-b border-dotted border-slate-400">Aspirational Dist.</span>
                  <div className="absolute right-0 top-full mt-1 w-64 bg-slate-800 text-white text-xs font-normal normal-case rounded-lg p-2 opacity-0 group-hover:opacity-100 pointer-events-none z-50 shadow-xl transition-opacity">
                    NITI Aayog 112 Aspirational Districts. Shows: Covered / Total in state.
                  </div>
                </th>
                <th className="text-right font-medium text-slate-500 uppercase py-2 px-2 group relative cursor-help">
                  <span className="border-b border-dotted border-slate-400">Aspirational Blocks</span>
                  <div className="absolute right-0 top-full mt-1 w-64 bg-slate-800 text-white text-xs font-normal normal-case rounded-lg p-2 opacity-0 group-hover:opacity-100 pointer-events-none z-50 shadow-xl transition-opacity">
                    NITI Aayog 499 Aspirational Blocks. Shows: Covered / Total in state.
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedState.map((r, i) => (
                <tr key={i} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                  <td className="py-2 px-2 text-slate-400 text-[11px]">{i + 1}</td>
                  <td className="py-2 px-2 font-medium text-slate-800">
                    {r.state}
                    {r.is_estimate && <span className="text-amber-500 ml-0.5 text-xs" title="Proportional estimate">*</span>}
                  </td>
                  <td className="py-2 px-2 text-slate-500">{r.zone}</td>
                  <td className="py-2 px-2 text-right">
                    <button
                      type="button"
                      data-testid={`state-grants-link-${r.state}`}
                      onClick={() => handleGrantsCellClick(r.state)}
                      className="text-[#B71C1C] font-medium underline decoration-dotted underline-offset-2 hover:decoration-solid hover:text-[#8a0e0e] transition-colors cursor-pointer"
                      title={`View grants in ${r.state}`}
                    >
                      {r.grants}
                    </button>
                  </td>
                  <td className="py-2 px-2 text-right font-medium text-[#B71C1C]">{fmtCr(r.allocation_cr)}</td>
                  <td className="py-2 px-2 text-right text-slate-600">{fmtCr(r.disbursed_cr)}</td>
                  <td className="py-2 px-2 text-right">
                    {r.disbursement_rate != null ? (
                      <span className={`px-1.5 py-0.5 rounded ${r.disbursement_rate >= 70 ? "bg-green-100 text-green-700" : r.disbursement_rate >= 40 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                        {r.disbursement_rate}%
                      </span>
                    ) : "–"}
                  </td>
                  <td className="py-2 px-2 text-right text-slate-500">{r.districts || "–"}</td>
                  <td className="py-2 px-2 text-right text-slate-500">{r.blocks || "–"}</td>
                  <td className="py-2 px-2 text-right">
                    {r.ad_total > 0 ? (
                      <span className={`${r.ad_covered > 0 ? "text-[#B71C1C] font-medium" : "text-slate-600"}`}>
                        {r.ad_covered || 0}/{r.ad_total}
                      </span>
                    ) : "–"}
                  </td>
                  <td className="py-2 px-2 text-right">
                    {r.ab_total > 0 ? (
                      <span className={`${r.ab_covered > 0 ? "text-purple-600 font-medium" : "text-slate-600"}`}>
                        {r.ab_covered || 0}/{r.ab_total}
                      </span>
                    ) : "–"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          ) : (
          <table className="w-full text-xs" data-testid="district-distribution-table">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="text-left font-medium text-slate-500 uppercase py-2 px-2 w-8">SN</th>
                <Th col="state" align="left">State</Th>
                <Th col="district" align="left">District</Th>
                <Th col="grants">Grants</Th>
                <Th col="allocation_cr">Sanctioned</Th>
                <Th col="disbursed_cr">Disbursed</Th>
                <Th col="disbursement_rate">Disb %</Th>
                <Th col="blocks">Blocks</Th>
                <Th col="is_aspirational">Aspirational</Th>
              </tr>
            </thead>
            <tbody>
              {sortedDistrict.map((r, i) => (
                <tr key={i} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                  <td className="py-2 px-2 text-slate-400 text-[11px]">{i + 1}</td>
                  <td className="py-2 px-2 font-medium text-slate-800">{r.state}</td>
                  <td className="py-2 px-2 text-slate-700">{r.district}</td>
                  <td className="py-2 px-2 text-right">
                    <button
                      type="button"
                      data-testid={`district-grants-link-${r.state}-${r.district}`}
                      onClick={() => handleGrantsCellClick(r.state, r.district)}
                      className="text-[#B71C1C] font-medium underline decoration-dotted underline-offset-2 hover:decoration-solid hover:text-[#8a0e0e] transition-colors cursor-pointer"
                      title={`View grants in ${r.district}, ${r.state}`}
                    >
                      {r.grants}
                    </button>
                  </td>
                  <td className="py-2 px-2 text-right font-medium text-[#B71C1C]">{fmtCr(r.allocation_cr)}</td>
                  <td className="py-2 px-2 text-right text-slate-600">{fmtCr(r.disbursed_cr)}</td>
                  <td className="py-2 px-2 text-right">
                    {r.disbursement_rate != null ? (
                      <span className={`px-1.5 py-0.5 rounded ${r.disbursement_rate >= 70 ? "bg-green-100 text-green-700" : r.disbursement_rate >= 40 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                        {r.disbursement_rate}%
                      </span>
                    ) : "–"}
                  </td>
                  <td className="py-2 px-2 text-right text-slate-500">{r.blocks || "–"}</td>
                  <td className="py-2 px-2 text-right">
                    {r.is_aspirational
                      ? <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-700 font-medium">Yes</span>
                      : <span className="text-slate-400">No</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          )}
        </div>
      </SectionHeader>
        );
      })()}

      {/* Grants Table — visible when a state is selected */}
      {geoDetail && (
        <GrantsTableSection ref={grantsTableRef} filters={grantsTableFilters} extraTitle={geoDetail.label} />
      )}

      {/* Pan India drill-down — visible when user clicks Grants on Pan India tab or heatmap row */}
      {panIndiaDrillScope && (
        <div ref={panIndiaDrillRef}>
        <SectionHeader
          title={panIndiaDrillScope === "rail_sahyog" ? "Rail Sahyog (29-State Apportioned)" : "Pan India Grants"}
          subtitle={panIndiaDrillScope === "rail_sahyog"
            ? "G-70067 — kept apportioned across 29 states per client decision."
            : "Grants with nation-wide reach — segregated from state-wise rollups."}
          collapsible defaultOpen={true}
          headerRight={
            <div className="flex items-center gap-2">
              <button
                data-testid="pan-india-drill-close"
                onClick={() => { setPanIndiaDrillScope(null); setPanIndiaGrants([]); }}
                className="text-[11px] px-2.5 py-1 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-[#B71C1C] hover:text-[#B71C1C] transition-colors"
                title="Close drill-down"
              >
                <X size={11} className="inline mr-1" />Close
              </button>
              <button
                data-testid="pan-india-drill-export"
                onClick={() => {
                  const headers = ["SN","Grant ID","Project Title","Grantee","Theme","Trust","Status","FY","Sanctioned (Cr)","Disbursed (Cr)","Disb %"];
                  const rows = panIndiaGrants.map((r, i) => [
                    i+1, r.grant_id, r.project_title || "", r.grantee || "", r.theme || "", r.trust_name || "", r.status || "", r.fy || "",
                    r.sanctioned_cr, r.disbursed_cr, r.disbursement_rate != null ? r.disbursement_rate + "%" : ""
                  ]);
                  const csv = [headers, ...rows].map(row => row.map(c => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
                  const blob = new Blob([csv], { type: "text/csv" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a"); a.href = url; a.download = `pan_india_grants_${panIndiaDrillScope}.csv`; a.click();
                  URL.revokeObjectURL(url);
                }}
                className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-[#B71C1C] hover:text-[#B71C1C] transition-colors"
              >
                <Download size={12} />
                Export CSV
              </button>
            </div>
          }
        >
          {panIndiaGrantsLoading ? (
            <div className="flex items-center justify-center py-10">
              <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-[#B71C1C]" />
            </div>
          ) : panIndiaGrants.length === 0 ? (
            <div className="text-center text-sm text-slate-500 py-10">No Pan India grants for the current filters.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="pan-india-grants-table">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50">
                    <th className="text-left font-medium text-slate-500 uppercase py-2 px-2 w-8">SN</th>
                    <th className="text-left font-medium text-slate-500 uppercase py-2 px-2">Grant ID</th>
                    <th className="text-left font-medium text-slate-500 uppercase py-2 px-2">Project Title</th>
                    <th className="text-left font-medium text-slate-500 uppercase py-2 px-2">Grantee</th>
                    <th className="text-left font-medium text-slate-500 uppercase py-2 px-2">Theme</th>
                    <th className="text-left font-medium text-slate-500 uppercase py-2 px-2">Trust</th>
                    <th className="text-left font-medium text-slate-500 uppercase py-2 px-2">Status</th>
                    <th className="text-right font-medium text-slate-500 uppercase py-2 px-2">Sanctioned</th>
                    <th className="text-right font-medium text-slate-500 uppercase py-2 px-2">Disbursed</th>
                  </tr>
                </thead>
                <tbody>
                  {panIndiaGrants.map((r, i) => (
                    <tr key={r.grant_id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                      <td className="py-2 px-2 text-slate-400 text-[11px]">{i + 1}</td>
                      <td className="py-2 px-2 font-mono text-slate-700">
                        {r.grant_id}
                        {r.is_rail_sahyog && (
                          <span className="ml-2 px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[9px] font-semibold border border-blue-200">29-State</span>
                        )}
                      </td>
                      <td className="py-2 px-2 text-slate-800 max-w-[280px] truncate" title={r.project_title}>{r.project_title}</td>
                      <td className="py-2 px-2 text-slate-700 max-w-[200px] truncate" title={r.grantee}>{r.grantee}</td>
                      <td className="py-2 px-2 text-slate-500 whitespace-nowrap">{displayTheme ? displayTheme(r.theme) : r.theme}</td>
                      <td className="py-2 px-2 text-slate-500 whitespace-nowrap">{r.trust_name}</td>
                      <td className="py-2 px-2">
                        <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${/ongoing|active/i.test(r.status) ? "bg-green-100 text-green-700" : /closed|completed/i.test(r.status) ? "bg-slate-100 text-slate-600" : "bg-amber-100 text-amber-700"}`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="py-2 px-2 text-right font-medium text-[#B71C1C] whitespace-nowrap">{fmtCr(r.sanctioned_cr)}</td>
                      <td className="py-2 px-2 text-right text-slate-600 whitespace-nowrap">{fmtCr(r.disbursed_cr)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionHeader>
        </div>
      )}
    </div>
  );
}
